import React, { useEffect, useState } from 'react';
import { Inputs, PackageType, BuildPayType, IndustryType, RevenueCadence, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
interface CalculatorInputsProps {
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
  onReset?: () => void;
  onReview?: () => void;
  showActionButtons?: boolean;
}
export function CalculatorInputs({
  inputs,
  onChange,
  onReset,
  onReview,
  showActionButtons = true
}: CalculatorInputsProps) {
  const [expandedSections, setExpandedSections] = useState({
    companyInfo: true,
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModel: false
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Handle numeric input changes - allow empty string, convert to number only when valid
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    // If empty, set to empty string to allow clearing
    if (value === '') {
      onChange({
        ...inputs,
        [field]: ''
      });
      return;
    }
    // Otherwise parse as float
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({
        ...inputs,
        [field]: numValue
      });
    }
  };
  // Update industry-specific defaults when industry changes
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    onChange({
      ...inputs,
      industry: newIndustry,
      ticket: defaults.ticket,
      gpm: defaults.gpm,
      salesFirst90Total: defaults.salesFirst90Total,
      salesVetMonthly: defaults.salesVetMonthly,
      revenueCadence: defaults.revenueCadence,
      residualTicket: defaults.residualTicket
    });
  };
  return <div className="bg-white border-2 border-gray-200 rounded p-4 md:p-6 shadow-lg max-w-4xl mx-auto" data-id="element-122">
      <div className="space-y-4" data-id="element-123">
        {/* Company Information - Single Row (No Collapsible) */}
        <div className="bg-gray-50 p-3 rounded border-2 border-gray-200" data-id="element-124">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-125">
            {/* Company Name */}
            <div data-id="element-126">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-127">
                Company Name
              </label>
              <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} placeholder="Enter company name" className="w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-128" />
            </div>

            {/* Industry */}
            <div data-id="element-129">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-130">
                Industry
              </label>
              <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-131">
                <option value="" data-id="element-132">Select</option>
                <option data-id="element-133">Windows</option>
                <option data-id="element-134">Roofing</option>
                <option data-id="element-135">Solar (Sales)</option>
                <option data-id="element-136">Solar (Vertical)</option>
                <option data-id="element-137">Solar (Community)</option>
                <option data-id="element-138">Pest</option>
                <option data-id="element-139">Fiber</option>
                <option data-id="element-140">Deregulated Energy</option>
                <option data-id="element-141">Hardscaping</option>
                <option data-id="element-142">Landscaping</option>
                <option data-id="element-143">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Retention Modeling - Collapsible */}
        <div data-id="element-144">
          <div className="flex items-center justify-between p-3 bg-purple-50 rounded cursor-pointer hover:bg-purple-100 transition-colors border-2 border-purple-200" onClick={() => toggleSection('retentionModel')} data-id="element-145">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-146">
              Retention Modeling
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-147">
              {expandedSections.retentionModel ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.retentionModel && <div className="mt-3 bg-purple-50 p-3 rounded border-2 border-purple-200" data-id="element-148">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-149">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-150">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-151">
                    Hired to Launched
                  </label>
                  <div className="w-56 relative" data-id="element-152">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-153" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-154">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-155">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-156">
                    Launched to Day 30
                  </label>
                  <div className="w-56 relative" data-id="element-157">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-158" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-159">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-160">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-161">
                    Day 30 to Day 90
                  </label>
                  <div className="w-56 relative" data-id="element-162">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-163" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-164">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-165">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-166">
                    Day 90 to 1 Year
                  </label>
                  <div className="w-56 relative" data-id="element-167">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-168" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-169">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Sales Performance - Collapsible */}
        <div data-id="element-170">
          <div className="flex items-center justify-between p-3 bg-amber-50 rounded cursor-pointer hover:bg-amber-100 transition-colors border-2 border-amber-200" onClick={() => toggleSection('salesPerformance')} data-id="element-171">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-172">
              Sales Performance
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-173">
              {expandedSections.salesPerformance ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.salesPerformance && <div className="mt-3 bg-amber-50 p-3 rounded border-2 border-amber-200" data-id="element-174">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-175">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-176">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-177">
                    Sales per 90 Day Rep
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesFirst90Total} onChange={e => handleNumericChange('salesFirst90Total', e.target.value)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-178" />
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-179">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-180">
                    Monthly Sales per Veteran
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => handleNumericChange('salesVetMonthly', e.target.value)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-181" />
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-182">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-183">
                    Pullthrough / Realization
                  </label>
                  <div className="w-56 relative" data-id="element-184">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-185" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-186">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Revenue & Margins - Collapsible */}
        <div data-id="element-187">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100 transition-colors border-2 border-green-200" onClick={() => toggleSection('revenueMargins')} data-id="element-188">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-189">
              Revenue & Margins
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-190">
              {expandedSections.revenueMargins ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.revenueMargins && <div className="mt-3 bg-green-50 p-3 rounded border-2 border-green-200" data-id="element-191">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-192">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-193">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-194">
                    Revenue Cadence
                  </label>
                  <select value={inputs.revenueCadence} onChange={e => handleChange('revenueCadence', e.target.value as RevenueCadence)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-195">
                    <option value="one-time" data-id="element-196">One-Time Sale</option>
                    <option value="monthly" data-id="element-197">Monthly Residual</option>
                    <option value="quarterly" data-id="element-198">Quarterly Residual</option>
                  </select>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-199">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-200">
                    {inputs.revenueCadence === 'one-time' ? 'Average Ticket (Revenue per Sale)' : 'Initial Sale Ticket'}
                  </label>
                  <div className="w-56 relative" data-id="element-201">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-202">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 pl-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-203" />
                  </div>
                </div>

                {inputs.revenueCadence !== 'one-time' && <>
                    <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-204">
                      <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-205">
                        {inputs.revenueCadence === 'monthly' ? 'Monthly Residual Ticket' : 'Quarterly Residual Ticket'}
                      </label>
                      <div className="w-56 relative" data-id="element-206">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-207">
                          $
                        </span>
                        <input type="number" min="0" step="10" value={inputs.residualTicket || 0} onChange={e => handleNumericChange('residualTicket', e.target.value)} className="w-full p-2 pl-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-208" />
                      </div>
                    </div>

                    {inputs.revenueCadence === 'monthly' && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-209">
                        <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-210">
                          Monthly Attrition
                        </label>
                        <div className="w-56 relative" data-id="element-211">
                          <input type="number" min="0" max="100" step="1" value={inputs.monthlyAttrition || 2} onChange={e => handleNumericChange('monthlyAttrition', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-212" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-213">
                            %
                          </span>
                        </div>
                      </div>}

                    {inputs.revenueCadence === 'quarterly' && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-214">
                        <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-215">
                          Quarterly Attrition
                        </label>
                        <div className="w-56 relative" data-id="element-216">
                          <input type="number" min="0" max="100" step="1" value={inputs.quarterlyAttrition || 5} onChange={e => handleNumericChange('quarterlyAttrition', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-217" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-218">
                            %
                          </span>
                        </div>
                      </div>}
                  </>}

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-219">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-220">
                    Gross Profit Margin
                  </label>
                  <div className="w-56 relative" data-id="element-221">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-222" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-223">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {showActionButtons && onReset && <>
            <div className="border-t-2 border-gray-200 pt-4" data-id="element-224">
              <button onClick={onReset} className="w-full px-4 py-2.5 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-bold text-sm transition-colors active:translate-y-[1px]" data-id="element-225">
                Reset to Defaults
              </button>
            </div>

            <div className="text-sm text-gray-600 leading-relaxed border-t-2 border-gray-200 pt-4" data-id="element-226">
              LMS note: access to a Learning Management System is included in
              all packages. In Edge it's more "DIY reference"; in
              Advantage/Summit it reinforces hands-on execution.
            </div>
          </>}

        {onReview && <div className="border-t-2 border-gray-200 pt-4" data-id="element-227">
            <button onClick={onReview} className="w-full py-3.5 md:py-4 rounded font-bold text-base md:text-lg transition-all shadow-lg bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer" data-id="element-228">
              Review Your Numbers →
            </button>
          </div>}
      </div>
    </div>;
}